/**
 * View Pie Chart
 *
 * Compact donut chart of labeled, colored bins with a legend (swatch · name ·
 * percent). Used by the View surface for composition distributions — land cover
 * (nominal classes) and treeline (Below/Near/Above) — where parts-of-a-whole
 * reads better than bar height. Slices follow the given bin order so ordered
 * data (e.g. treeline low→high) stays in order around the ring.
 */

import { LitElement, html, svg, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import type { DistributionBin } from '../../lib/geometry-view.js'

@customElement('view-pie-chart')
export class ViewPieChart extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
    }

    .heading {
      font-size: 11px;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.85);
      margin-bottom: 6px;
    }

    .donut {
      width: 96px;
      height: 96px;
      display: block;
      margin: 0 auto 8px;
    }

    .legend {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    .lrow {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 11px;
      color: rgba(255, 255, 255, 0.9);
    }

    .swatch {
      width: 10px;
      height: 10px;
      border-radius: 2px;
      flex-shrink: 0;
    }

    .lname {
      flex: 1;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-weight: 600;
    }

    .lpct {
      font-weight: 700;
      flex-shrink: 0;
    }

    .empty {
      font-size: 11px;
      opacity: 0.5;
    }
  `

  @property({ type: String }) heading = ''
  @property({ attribute: false }) bins: DistributionBin[] = []

  override render(): unknown {
    const total = this.bins.reduce((s, b) => s + b.value, 0)
    if (total === 0) {
      return html`<div class="heading">${this.heading}</div>
        <div class="empty">No data</div>`
    }

    // Exact fractions drive the geometry (so rounding never opens a seam); the
    // legend rounds only for display. dash/offset use pathLength=100, the
    // canonical donut recipe: offset 25 starts the first slice at 12 o'clock,
    // each subsequent slice begins where the previous ended (clockwise).
    let cum = 0
    const segs = this.bins
      .filter(b => b.value > 0)
      .map(b => {
        const frac = b.value / total
        const seg = {
          color: b.color,
          dash: frac * 100,
          offset: 25 - cum * 100,
          label: b.label,
          pct: Math.round(frac * 100),
          hint: b.hint,
        }
        cum += frac
        return seg
      })

    return html`
      <div class="heading">${this.heading}</div>
      <svg class="donut" viewBox="0 0 36 36" role="img" aria-label=${this.heading}>
        ${segs.map(
          s => svg`<circle
            cx="18"
            cy="18"
            r="14"
            fill="none"
            stroke=${s.color}
            stroke-width="7"
            pathLength="100"
            stroke-dasharray="${s.dash} ${100 - s.dash}"
            stroke-dashoffset=${s.offset}
          >
            <title>${s.hint ? `${s.pct}% — ${s.hint}` : `${s.label}: ${s.pct}%`}</title>
          </circle>`
        )}
      </svg>
      <div class="legend">
        ${segs.map(
          s =>
            html`<div class="lrow" title=${s.hint ?? s.label}>
              <span class="swatch" style="background:${s.color}"></span>
              <span class="lname">${s.label}</span>
              <span class="lpct">${s.pct}%</span>
            </div>`
        )}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'view-pie-chart': ViewPieChart
  }
}
